import * as Sentry from '@sentry/vue'
import * as pkg from '~/package.json'
import { useRouter } from '#vue-router'
import { useRuntimeConfig } from '#app'
import { useCountryConfig } from '~/composables/useCountryConfig'

declare module '#app' {
  // @ts-expect-error For some reason that might throw an error, though it's valid according to the docs
  interface NuxtApp {
    $sentry: {
      setContext: typeof Sentry.setContext
      setUser: typeof Sentry.setUser
      setTag: typeof Sentry.setTag
      addBreadcrumb: typeof Sentry.addBreadcrumb
      captureException: typeof Sentry.captureException
      captureMessage: typeof Sentry.captureMessage
    }
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const dsn = useCountryConfig().sentryDsn
  const { environment, sentryEnabled, country } = useRuntimeConfig().public
  const release = `shop-frontend@${pkg.version}`
  const router = useRouter()

  if (!sentryEnabled || !dsn)
    return {
      provide: {
        sentry: {
          setContext: (() => {}) as typeof Sentry.setContext,
          setUser: (() => {}) as typeof Sentry.setUser,
          setTag: (() => {}) as typeof Sentry.setTag,
          addBreadcrumb: (() => {}) as typeof Sentry.addBreadcrumb,
          captureException: (() => '') as typeof Sentry.captureException,
          captureMessage: (() => '') as typeof Sentry.captureMessage,
        },
      },
    }

  Sentry.init({
    app: nuxtApp.vueApp,
    release,
    environment,
    dsn,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 0.5, // Change in prod
    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      'https://shop-staging.heiland.com',
      'https://shop.staging.vetmazing.com',
      'https://shop.heiland.com',
      'https://shop.vetmazing.com',
    ],
    replaysSessionSampleRate: 0.25,
    replaysOnErrorSampleRate: 1.0, // Change in prod if necessary

    ignoreErrors: ['401 Unauthorized', '422 Unprocessable Entity'],
  })

  Sentry.setTag('environment-locale', country)

  nuxtApp.vueApp.config.errorHandler = (error: any) => {
    Sentry.captureException(error)
  }

  return {
    provide: {
      sentry: {
        setContext: Sentry.setContext,
        setUser: Sentry.setUser,
        setTag: Sentry.setTag,
        addBreadcrumb: Sentry.addBreadcrumb,
        captureException: Sentry.captureException,
        captureMessage: Sentry.captureMessage,
      },
    },
  }
})
